/**
 * URL *shapes* that more than one process has to agree on.
 *
 * This module holds paths, never origins. The distinction is the whole point and it is
 * the resolution of a real boundary collision found while building c31:
 *
 *   - `resolver_entries.entry.platformFallbackUrl` is an **absolute** URL, written by
 *     the entry builder in `packages/db`.
 *   - `apps/api/src/urls.ts` is the API's sole reader of the base URLs, and an eslint
 *     rule keeps it that way — every absolute URL the service emits is built from
 *     configuration rather than from the request (c21's finding).
 *   - `packages-never-import-apps` forbids `packages/db` importing that module.
 *
 * Three true things that cannot all be satisfied by putting the URL builder on either
 * side. What resolves them is splitting the fact in two: the **shape** is one pure
 * function here, importable by the builder and by the API alike; the **origin** stays
 * behind `urls.ts`, which feeds it in. One owner for the shape, one reader for the
 * config, and no boundary crossed.
 *
 * The alternatives were considered and rejected, recorded so they are not re-proposed:
 * moving the builder into `apps/api` fails because the seed lives in `packages/db` and
 * packages may not import apps, so there would be a second builder — and *"there is no
 * second builder"* is the reason c31 exists at all. A config-reading URL module inside
 * `packages/db` fails because a second reader of the base URLs is one decision with two
 * answers, which is precisely the failure `urls.ts`'s own header describes.
 */
/**
 * The platform-hosted destination for a GTIN, as a path on the resolver edge.
 *
 * This is where a scan lands when the tenant's own destination is not the answer —
 * the interstitial-branded page for a `degraded` tenant, PIP-lite for a `churned` one,
 * and the "no longer available" page for a tombstone (§3.4, §3.6). One path, because
 * the destination decides what to render: the resolver only redirects, and giving it
 * three fields to choose between would put product copy on the hot path.
 *
 * **The origin is always the resolver edge (`pkgedge.com`), never the brand origin.**
 * Any URI that can end up printed on a carton lives on the permanent domain — that is
 * the domain split, and it is not negotiable per call site, which is why this function
 * takes no base and the two callers each supply the same one from config.
 *
 * @param gtin14 A GTIN-14. Not validated here: both callers hold a value that has
 *   already passed `resolver_entries_gtin14_check` or `products.gtin14`'s own CHECK,
 *   and a second, weaker validation in a string builder would be a third spelling of a
 *   rule the database already enforces.
 */
export function platformFallbackPath(gtin14) {
    return `/p/${gtin14}`;
}
/**
 * A GS1 Digital Link URI's path — the thing a scan of the barcode actually requests.
 *
 * Split from its origin for the same reason {@link platformFallbackPath} is, and c34 is
 * where the second reader appeared: the dashboard's *"View live page"* action has to open
 * **exactly what a scan gets**, or the button is a claim rather than a link. The SPA cannot
 * read `RESOLVER_BASE_URL` — it is a browser bundle, and a `VITE_RESOLVER_BASE_URL` beside
 * it would be a second spelling of one value, which is the failure `apps/api/src/urls.ts`'s
 * own header is written about. So the API hands the origin to the dashboard as data and
 * both processes build the path from this one function.
 *
 * `/01/` is the GS1 Application Identifier for a GTIN and is not ours to choose; it is
 * spelled here once so that a typo in it is one typo rather than two.
 *
 * ## The qualifiers, added in c35, and why they belong here rather than at a call site
 *
 * `22` / `10` / `21` — CPV, batch/lot, serial — in **that order and no other**. The order
 * is normative: `packages/gs1` rejects a Digital Link URI whose qualifiers appear in any
 * other sequence, so a path built the wrong way round is not a stylistic variant, it is a
 * URI the resolver will refuse.
 *
 * It moved here because c35 was about to write the **third** spelling of it. `apps/api`'s
 * link ops had one for the refusal copy — *"plain scans of `/01/G/10/LOT42` already have a
 * destination"* — the merge preview needed one to echo the scan back, and the simulator
 * needs one to label its own controls. Three renderings of one AI ordering, in three
 * packages, none of which would notice the others drifting. Now: one.
 *
 * Still a **rendering rather than a parser** — nothing here validates a qualifier's
 * charset, because every caller holds a value that has already passed `links_lot_check`,
 * `CHARSET_X` or the scan that produced it. A weaker second validation in a string builder
 * is the shape this file's other two functions already refuse.
 *
 * @param gtin14 A GTIN-14, already past whichever CHECK the caller's row carries. Not
 *   validated here, for the reason {@link platformFallbackPath} gives.
 * @param qualifiers Absent, `undefined` and `null` all mean "not present in the scanned
 *   code" — the same three spellings the link writer's grain accepts, so a caller holding
 *   a row's nullable columns can pass them straight in.
 */
export function digitalLinkPath(gtin14, qualifiers) {
    let path = `/01/${gtin14}`;
    if (qualifiers?.cpv != null)
        path += `/22/${qualifiers.cpv}`;
    if (qualifiers?.lot != null)
        path += `/10/${qualifiers.lot}`;
    if (qualifiers?.serial != null)
        path += `/21/${qualifiers.serial}`;
    return path;
}
/**
 * The built-in recall notice for one recall, as a path on the resolver edge.
 *
 * **On the resolver edge for the same reason `platformFallbackPath` is**, and the
 * reasoning is sharper here rather than weaker. This is where a plain camera scan of a
 * recalled unit is redirected, so it is answered on behalf of a code already printed —
 * and it has to keep being answered for as long as those units exist, which is the whole
 * argument for the domain split. A notice on the brand origin would tie a safety page to
 * a rebrandable domain.
 *
 * It is keyed on the **recall** as well as the GTIN because §3.8's trail outlives the
 * recall's active life: the evidence record is retrievable forever, including post-lift,
 * and a URL that named only the GTIN would mean whatever the newest recall on it says.
 *
 * A recall may point somewhere else entirely — `recalls.external_url` is the brand's own
 * or a regulator's notice — in which case this path is never built. The choice is the
 * route's, not this function's.
 *
 * @param gtin14 A GTIN-14, already past `recalls_gtin14_check`.
 * @param recallId The recall's UUID. Not encoded: it comes from `uuidv7()` and the column
 *   is `uuid`, so there is no spelling of it that needs escaping — and encoding it here
 *   would imply this function accepts values it does not.
 */
export function recallNoticePath(gtin14, recallId) {
    return `/p/${gtin14}/recall/${recallId}`;
}
//# sourceMappingURL=urls.js.map